import { announcementService, LOGIN_PORTAL_URL } from '@moal/api'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useReducer } from 'react'
import {
  LOGGED_OUT,
  PAGE_LOAD,
  USER
} from '../constants/actions/authenticationActions'
import { PRODUCT_MAP } from '../constants/products'
import {
  authenticationReducer,
  initialState
} from '../reducers/authenticationReducer'
import {
  postAuthLogin,
  postAuthPersist,
  postAuthSignUp
} from '../utils/APIs/authorization'
import { getSelf, putUser } from '../utils/APIs/users'
import { PRODUCT_CODE } from '../utils/ENV'
import { preserveWhitelistedParams } from '../utils/utilities'
import { AnalyticsContext } from './analyticsContext'
import { SiteConfigContext } from './siteConfigContext'

export const AuthenticationContext = React.createContext({})

export const AuthenticationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authenticationReducer, initialState)
  const { freeEnabled, productsService } = useContext(SiteConfigContext)
  const { persistTrackingParams } = useContext(AnalyticsContext)
  const { search } = window.location
  const urlParams = new URLSearchParams(search)
  const code = urlParams.get('code')
  const jwt = localStorage.getItem('authToken')

  useEffect(() => {
    processAuth()
  }, [jwt, code])

  const processAuth = async () => {
    if (jwt) {
      await persist()
    } else if (code) {
      await login()
    }
    // After we process the auth, we want to handle any query params
    // First we will persist any tracking params. We will blacklist any non-tracking params
    persistTrackingParams(search, ['code', 'channel'])
    // Next we will remove query params from the URL, leaving only whitelisted params in place
    preserveWhitelistedParams(search, ['channel', 'testing'])

    dispatch({ type: PAGE_LOAD, payload: false })
  }

  const login = async () => {
    try {
      // Extract and validate the code.
      const [itemNumber, authCode] = code.split(':')
      if (itemNumber !== PRODUCT_CODE) {
        console.error('Invalid item number')
        dispatch({
          type: LOGGED_OUT,
          payload: 'Something went wrong please try again.'
        })
        return null
      }

      const response = await postAuthLogin(authCode)
      if (response && response.status === 201) {
        const { user, token } = response.data
        localStorage.setItem('authToken', token)
        announcementService.initializeAuth()
        updateUser(user)
      } else {
        dispatch({
          type: LOGGED_OUT,
          payload:
            response?.data?.errors?.[0]?.message ??
            'Something went wrong please try again.'
        })
      }
    } catch (error) {
      console.error('Login error:', error)
      // You can check error.response to get more details.
      dispatch({
        type: LOGGED_OUT,
        payload:
          error.response?.data?.errors?.[0]?.message ??
          'Something went wrong please try again.'
      })
    }
  }

  const signUp = async ({ email, signUpList, freeListSignUp }) => {
    const isValid = email && signUpList?.listCodes && signUpList?.sourceId

    if (!isValid) {
      return {
        success: false,
        data: {
          message: "Couldn't process signup...",
          extras: { support: true }
        }
      }
    }

    try {
      const response = await postAuthSignUp(email, signUpList, freeListSignUp)

      if (response?.status === 201) {
        const { user, token } = response.data
        localStorage.setItem('authToken', token)
        announcementService.initializeAuth()
        updateUser(user)
        return {
          success: true,
          data: response.data
        }
      }

      return {
        success: false,
        data: response?.data?.errors?.[0]?.message || 'Unknown error'
      }
    } catch (error) {
      return {
        success: false,
        data: {
          message:
            error.response?.data?.errors?.[0]?.message ??
            "Couldn't process signup...",
          extras: { support: true }
        }
      }
    }
  }

  const persist = async () => {
    // check if there is data in LS
    const token = localStorage.getItem('authToken')
    if (token) {
      const response = await postAuthPersist(token)
      if (response && response.status === 200) {
        dispatch({ type: USER, payload: response.data })
        announcementService.initializeAuth()
      } else {
        localStorage.removeItem('authToken')
        announcementService.clearAuthToken()
        dispatch({
          type: LOGGED_OUT,
          payload:
            response?.data?.errors?.[0]?.message ?? 'Session has expired.'
        })
      }
    }
  }

  const logout = async () => {
    localStorage.removeItem('authToken')
    announcementService.clearAuthToken()
    if (freeEnabled) {
      window.location.reload(true)
    } else {
      window.location.href = `${LOGIN_PORTAL_URL}?logout=true&from=${PRODUCT_CODE}`
    }
  }

  const setFreshUserData = async () => {
    const response = await getSelf()
    if (response && response.status === 200) {
      updateUser(response.data)
    }
  }

  const updateUser = async (newData) => {
    dispatch({ type: USER, payload: newData })
  }

  const endTimeOut = async () => {
    const response = await putUser(
      state.user._id,
      'timeout',
      false,
      'moderation'
    )
    if (response && response.status === 200) {
      updateUser(response.data)
    }
  }

  const getSubType = ({ user = null, anyOpenHouse = false }) => {
    const subscriptions = (user ?? state.user)?.userService?.subscriptions
    if (!subscriptions?.length) return null

    const findSubscription = (itemNumber) =>
      subscriptions.find((sub) => sub.itemNumber === itemNumber) ?? null

    const getSubTypeForItem = (itemNumber) =>
      findSubscription(itemNumber)?.subType ?? null

    // 1) Direct product subscription
    const directProductSubType = getSubTypeForItem(PRODUCT_CODE)
    if (directProductSubType) return directProductSubType

    const thisProductConfig = productsService?.[PRODUCT_MAP.THIS_PRODUCT]

    // 2) access product subscription
    const accessItemNumber = thisProductConfig?.access?.itemNumber
    if (accessItemNumber) {
      const accessProductSubType = getSubTypeForItem(accessItemNumber)
      if (accessProductSubType) return accessProductSubType
    }

    // NOT SURE WHAT TO DO WITH SUBSCRIPTION ANY???

    // 3) If user has the open-house itemNumber subscription, return that itemNumber (existing behavior)
    const openHouseConfig = thisProductConfig?.openHouse
    const openHouseItemNumber = openHouseConfig?.itemNumber
    if (openHouseItemNumber) {
      const hasOpenHouseSubscription = Boolean(
        findSubscription(openHouseItemNumber)
      )
      if (hasOpenHouseSubscription) return openHouseItemNumber
    }

    // 4) Open house fallback (requires enabled + itemNumber + matching subtype)
    if (!openHouseConfig?.enabled || !openHouseConfig?.itemNumber) return null

    const openHouseSubType = getSubTypeForItem(openHouseConfig.itemNumber)
    if (!openHouseSubType) return null

    return anyOpenHouse ? openHouseConfig.itemNumber : openHouseSubType
  }

  return (
    <AuthenticationContext.Provider
      value={{
        user: state.user,
        getSubType,
        pageLoad: state.pageLoad,
        loggedOut: state.loggedOut,
        signUp,
        persist,
        logout,
        updateUser,
        setFreshUserData,
        endTimeOut
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  )
}

AuthenticationProvider.propTypes = {
  children: PropTypes.any
}
